import { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Text } from '@hopskipdrive/react-components';
import { DataGrid } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import { AssigneeAvatar } from '../../components/AssigneeSelect';
import IconChip from '../../components/IconChip';
import {
  StyledIncidentClassification,
  StyledIncidentPriority,
} from '../../components/IncidentClassificationDropdown/IncidentClassificationDropdown.styles';
import { Status } from '../../components/StatusSelect';
import { useGetOffenderTypes } from '../../hooks/useIncidentApi';
import {
  Incident,
  IncidentClassification,
  IncidentIndividual,
  IncidentSubclassification,
  IncidentTripAccount,
} from '../../types/incidents';
import { StyledAccount } from '../IncidentForm/IncidentForm.styles';

import { EmptyTableContainer, StyledIncidentTable } from './IncidentTable.styles';

interface IncidentTableProps {
  isFetching: boolean;
  isError: boolean;
  incidents: Incident[];
  activeRow: number;
  setActiveRow: (row: number) => void;
}

interface IncidentTableRow {
  id: number;
  status: string;
  description: {
    classification: IncidentClassification;
    subclassification?: IncidentSubclassification;
    description: string;
  };
  account: IncidentTripAccount;
  reportedParty: { type: string; offender: IncidentIndividual | undefined };
  assigned: IncidentIndividual;
  incident_date: string;
  reported_on: string;
}

const IncidentTable = ({
  isFetching,
  isError,
  incidents,
  activeRow,
  setActiveRow,
}: IncidentTableProps): ReactElement => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: offenderTypeResponse } = useGetOffenderTypes();

  const changeSortBy = (sortModel) => {
    const newParams = new URLSearchParams(searchParams);

    if (!sortModel.length) {
      newParams.delete('sortField');
      newParams.delete('sortDir');
    } else {
      newParams.set('sortField', sortModel[0].field);
      newParams.set('sortDir', sortModel[0].sort);
    }

    setSearchParams(newParams);
  };

  const setActiveRowClass = (params) => {
    if (params.id === activeRow) {
      return 'active';
    }

    return '';
  };

  const rows: IncidentTableRow[] = [];

  incidents.forEach((incident) => {
    const row: IncidentTableRow = {
      id: incident.id,
      status: incident.status,
      description: {
        classification: incident.classification,
        subclassification: incident.subclassification,
        description: incident.description,
      },
      account: incident.account,
      reportedParty: {
        type: incident.offenderType,
        offender: incident.offender,
      },
      assigned: incident.assignee,
      incident_date: format(parseISO(incident.incidentDate), 'MM/dd/yyyy'),
      reported_on: format(parseISO(incident.reportedOn), 'MM/dd/yyyy'),
    };

    rows.push(row);
  });

  const columns = [
    {
      field: 'status',
      type: 'string',
      sortable: false,
      flex: 0.6,
      renderHeader: (_) => <Text variant="subtitle2">Status</Text>,
      renderCell: (params) => (
        <Box className="status-cell">
          <Status value={params?.value} filled />
          <Text className="id" variant="subtitle2">
            #{params?.id}
          </Text>
        </Box>
      ),
    },
    {
      field: 'description',
      type: 'string',
      sortable: false,
      flex: 4,
      renderHeader: (_) => <Text variant="subtitle2">Description</Text>,
      renderCell: (params) => (
        <Box className="details-cell">
          <Box className="classification">
            <StyledIncidentPriority variant="body2" className={'p' + params?.value?.classification?.priority}>
              P{params?.value?.classification?.priority}
            </StyledIncidentPriority>
            <StyledIncidentClassification>
              <Text variant="subtitle2">
                {params?.value?.classification?.name}
                {params?.value?.subclassification?.id && <span>: {params?.value?.subclassification?.name}</span>}
              </Text>
            </StyledIncidentClassification>
          </Box>
          <Text variant="subtitle2" className="clamped">
            {params?.value?.description}
          </Text>
        </Box>
      ),
    },
    {
      field: 'account',
      type: 'string',
      sortable: false,
      flex: 1,
      renderHeader: (_) => <Text variant="subtitle2">Account</Text>,
      renderCell: (params) => (
        <Box className="account-cell">
          <StyledAccount variant="body2" className={params?.value?.type} />
          <Text variant="subtitle2">{params?.value?.name}</Text>
        </Box>
      ),
    },
    {
      field: 'reportedParty',
      type: 'string',
      sortable: false,
      flex: 2,
      renderHeader: (_) => <Text variant="subtitle2">Alleged Offender</Text>,
      renderCell: (params) => {
        return (
          <Box className="reportedParty-cell">
            <IconChip
              value={params?.value?.type}
              label={offenderTypeResponse?.data?.find((item) => item.value === params?.value?.type)?.label || ''}
            />
            <Text variant="subtitle2">
              {params?.value?.offender?.firstName} {params?.value?.offender?.lastName}
            </Text>
          </Box>
        );
      },
    },
    {
      field: 'assigned',
      type: 'string',
      sortable: false,
      flex: 0.5,
      renderHeader: (_) => <Text variant="subtitle2">Assigned</Text>,
      renderCell: (params) => <AssigneeAvatar assignee={params?.value} />,
    },
    {
      field: 'incident_date',
      type: 'string',
      sortable: true,
      flex: 0.75,
      renderHeader: (_) => <Text variant="subtitle2">Incident On</Text>,
      renderCell: (params) => <Text variant="body2">{params?.value}</Text>,
    },
    {
      field: 'reported_on',
      type: 'string',
      sortable: true,
      flex: 0.75,
      renderHeader: (_) => <Text variant="subtitle2">Reported On</Text>,
      renderCell: (params) => <Text variant="body2">{params?.value}</Text>,
    },
  ];

  return (
    <div>
      <StyledIncidentTable>
        <DataGrid
          loading={isFetching}
          rows={rows}
          columns={columns}
          onRowClick={(params) => setActiveRow(params?.row?.id)}
          onSortModelChange={(newSortModel) => changeSortBy(newSortModel)}
          sortingMode="server"
          getRowClassName={setActiveRowClass}
          disableColumnFilter
          disableColumnMenu
          disableVirtualization
          disableRowSelectionOnClick
          hideFooter
          slots={{
            noRowsOverlay: () => (
              <EmptyTableContainer>
                {!isError && <Text variant="subtitle2">No incidents found</Text>}
                {isError && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    message={'Could not load incidents. Please try again.'}
                    action={
                      <Button
                        color="primary"
                        size="small"
                        onClick={() => queryClient.invalidateQueries({ queryKey: ['getAllSafetyIncidents'] })}
                      >
                        Try Reloading
                      </Button>
                    }
                  />
                )}
              </EmptyTableContainer>
            ),
          }}
        />
      </StyledIncidentTable>
    </div>
  );
};

export default IncidentTable;
